import { EditOtherDistributionModal } from 'components/EditOtherDistributionModal';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimWorkOrderOtherPriceData, WorkOrderStatusEnum } from 'gql/graphql';
import { OtherCostCalculationTypeEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { tabItemsDistribution } from 'pages/Project/Common/types';

import { useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useBimMethodsDistributionOther } from '../BimOtherPriceModal/hooks/useBimMethodsDistributionOther';

export const BimWorkOrderDistributionModal = ({
    dataRow,
    isOpenModal,
    constructionTypeId,
    statusWorkOrder,
    refreshTable,
    onCloseModal,
    isFrameContract,
}: {
    isOpenModal: boolean;
    constructionTypeId?: number;
    statusWorkOrder: WorkOrderStatusEnum;
    refreshTable: () => void;
    onCloseModal: () => void;
    dataRow?: BimWorkOrderOtherPriceData;
    isFrameContract: boolean;
}) => {
    const {
        control,
        handleSubmit,
        setValue,
        setError,
        clearErrors,
        reset,
        watch,
        formState: { isDirty, errors },
    } = useForm({ defaultValues: {} });
    const [activeTab, setActiveTab] = useState(tabItemsDistribution[0]);

    const handleTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const handleSubmitWarningDialog = useCallback(() => {
        onCloseModal?.();
    }, [onCloseModal]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: handleSubmitWarningDialog,
        });

    const resetIncomingData = useCallback(() => {
        reset();
        refreshTable();
        onCloseModal();
    }, [onCloseModal, refreshTable, reset]);

    const {
        bimWorkOrderOtherDistributionData,
        bimWorkOrderOtherDistributionStatus,
        bimUpdateWorkOrderOtherDistributionStatus,
        onUpdateOtherDistribution,
        resetDataDialog,
    } = useBimMethodsDistributionOther({
        isOpenModal,
        dataRow,
        resetIncomingData,
        onCloseModal,
        statusWorkOrder,
    });

    const isUpdateFetching = useMemo(
        () => bimUpdateWorkOrderOtherDistributionStatus === RequestStatus.FETCHING,
        [bimUpdateWorkOrderOtherDistributionStatus],
    );

    const isDataFetching = useMemo(
        () => bimWorkOrderOtherDistributionStatus === RequestStatus.FETCHING,
        [bimWorkOrderOtherDistributionStatus],
    );

    const handleCloseModal = useCallback(() => {
        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            resetDataDialog();
        }
    }, [handleOpenConfirmationDialog, isDirty, resetDataDialog]);

    const isCalculating = useMemo(
        () =>
            bimWorkOrderOtherDistributionData?.data?.contractOtherCost?.calculatingType ===
            OtherCostCalculationTypeEnum.Calculation,
        [bimWorkOrderOtherDistributionData?.data?.contractOtherCost?.calculatingType],
    );

    return (
        <EditOtherDistributionModal
            handleCancel={handleCloseModal}
            onSubmit={onUpdateOtherDistribution}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            bimWorkOrderOtherDistributionData={bimWorkOrderOtherDistributionData?.data}
            isDataFetching={isDataFetching}
            currentConstructionTypeId={constructionTypeId}
            isOpenWarningDialog={isOpenConfirmationDialog}
            handleSubmitWarningDialog={handleActionQuery}
            handleCancelWarningDialog={handleCloseConfirmationDialog}
            formControl={control}
            formHandleSubmit={handleSubmit}
            setValue={setValue}
            isLoading={isUpdateFetching}
            statusWorkOrder={statusWorkOrder}
            watch={watch}
            workOrderOtherPriceId={dataRow?.id}
            contractOtherCostId={dataRow?.contractOtherCostId}
            isCalculating={isCalculating}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            isFrameContract={isFrameContract}
            costRounding={0}
        />
    );
};

BimWorkOrderDistributionModal.displayName = 'BimWorkOrderDistributionModal';
